import React, { useState, useEffect } from "react";
import { API } from "../../../config/api";
import { get, post } from "../../../lib/http";

const PaymentReconciliation = () => {
  const [dailyReport, setDailyReport] = useState(null);
  const [reconciliationResult, setReconciliationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);

  // Fetch daily report on mount
  useEffect(() => {
    fetchDailyReport();
  }, []);

  const fetchDailyReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await get(API.paymentReconciliation.dailyReport, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (res?.success) {
        setDailyReport(res.report);
      } else {
        setError(res?.message || "Failed to fetch report");
      }
    } catch (err) {
      setError(err?.message || "Error fetching daily report");
    } finally {
      setLoading(false);
    }
  };

  const runReconciliation = async (dryRun = false) => {
    if (!confirm(dryRun ? "Run reconciliation in DRY RUN mode?" : "Run reconciliation? This will process stuck payments.")) {
      return;
    }

    try {
      setReconciling(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await post(
        API.paymentReconciliation.reconcile,
        { dryRun, limit: 50 },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (res?.success) {
        setReconciliationResult(res.results);
        alert(
          `Reconciliation Complete!\n\n` +
          `✅ Successful: ${res.results.successful}\n` +
          `❌ Failed: ${res.results.failed}\n` +
          `⏰ Expired: ${res.results.expired}\n` +
          `📊 Total: ${res.results.total}`
        );
        // Refresh daily report
        fetchDailyReport();
      } else {
        setError(res?.message || "Reconciliation failed");
      }
    } catch (err) {
      setError(err?.message || "Error running reconciliation");
    } finally {
      setReconciling(false);
    }
  };

  const cleanupExpiredPayments = async () => {
    if (!confirm("Cleanup expired payments older than 24 hours?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await post(
        API.paymentReconciliation.cleanupExpired,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (res?.success) {
        alert(`Cleanup Complete! Expired ${res.count} payment links.`);
        fetchDailyReport();
      } else {
        setError(res?.message || "Cleanup failed");
      }
    } catch (err) {
      setError(err?.message || "Error cleaning up payments");
    } finally {
      setLoading(false);
    }
  };

  const sendDailySummary = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await post(
        API.paymentReconciliation.sendDailySummary,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (res?.success) {
        alert("Daily summary email sent successfully!");
      } else {
        setError(res?.message || "Failed to send summary");
      }
    } catch (err) {
      setError(err?.message || "Error sending summary");
    } finally {
      setLoading(false);
    }
  };

  const runAutoRecovery = async () => {
    if (!confirm("Run auto-recovery for stuck payments (48+ hours old)?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await post(
        API.paymentReconciliation.autoRecover,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (res?.success) {
        alert(
          `Auto-Recovery Complete!\n\n` +
          `Total: ${res.result.total}\n` +
          `✅ Recovered: ${res.result.recovered}\n` +
          `❌ Failed: ${res.result.failed}`
        );
        fetchDailyReport();
      } else {
        setError(res?.message || "Auto-recovery failed");
      }
    } catch (err) {
      setError(err?.message || "Error running auto-recovery");
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    if (!confirm("Create backup of all payment data?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const res = await post(
        API.paymentReconciliation.backup,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (res?.success) {
        alert(
          `Backup Created Successfully!\n\n` +
          `Records: ${res.backup.recordCount}\n` +
          `Timestamp: ${res.backup.timestamp}`
        );
      } else {
        setError(res?.message || "Backup failed");
      }
    } catch (err) {
      setError(err?.message || "Error creating backup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          💰 Payment Reconciliation
        </h1>
        <p className="text-gray-600">
          Monitor payment status and reconcile stuck transactions
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">❌ {error}</p>
        </div>
      )}

      {/* Daily Report */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="text-green-600 text-sm font-medium mb-1">
            ✅ Successful
          </div>
          <div className="text-3xl font-bold text-green-700">
            {loading ? "..." : dailyReport?.successful || 0}
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-red-600 text-sm font-medium mb-1">
            ❌ Failed
          </div>
          <div className="text-3xl font-bold text-red-700">
            {loading ? "..." : dailyReport?.failed || 0}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="text-yellow-600 text-sm font-medium mb-1">
            ⏳ Pending
          </div>
          <div className="text-3xl font-bold text-yellow-700">
            {loading ? "..." : dailyReport?.pending || 0}
          </div>
          {dailyReport?.pending > 10 && (
            <div className="text-xs text-yellow-600 mt-2">
              ⚠️ High pending count
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="text-blue-600 text-sm font-medium mb-1">
            💰 Revenue
          </div>
          <div className="text-2xl font-bold text-blue-700">
            ₹{loading ? "..." : dailyReport?.totalRevenue?.toFixed(2) || "0.00"}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            {dailyReport?.date || "Today"}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">🔧 Reconciliation Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => runReconciliation(false)}
            disabled={reconciling}
            className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {reconciling ? "⏳ Processing..." : "🔄 Run Reconciliation"}
          </button>

          <button
            onClick={() => runReconciliation(true)}
            disabled={reconciling}
            className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {reconciling ? "⏳ Processing..." : "🧪 Dry Run"}
          </button>

          <button
            onClick={cleanupExpiredPayments}
            disabled={loading}
            className="px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            🧹 Cleanup Expired
          </button>

          <button
            onClick={sendDailySummary}
            disabled={loading}
            className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            📧 Email Summary
          </button>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>• <strong>Run Reconciliation:</strong> Check Razorpay for stuck payments and process them</p>
          <p>• <strong>Dry Run:</strong> Preview what would be reconciled without making changes</p>
          <p>• <strong>Cleanup Expired:</strong> Mark old payment links as expired (24h+)</p>
          <p>• <strong>Email Summary:</strong> Send today's report to admin email</p>
        </div>
      </div>

      {/* Recovery & Backup Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">🛠️ Recovery & Backup</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={runAutoRecovery}
            disabled={loading}
            className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            🔧 Auto-Recover Stuck Payments
          </button>

          <button
            onClick={createBackup}
            disabled={loading}
            className="px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            💾 Create Backup
          </button>

          <button
            onClick={fetchDailyReport}
            disabled={loading}
            className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            🔄 Refresh Stats
          </button>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>• <strong>Auto-Recover:</strong> Automatically retry payments stuck for 48+ hours</p>
          <p>• <strong>Create Backup:</strong> Backup all payment data to secure storage</p>
          <p>• <strong>Refresh Stats:</strong> Reload today's payment statistics</p>
        </div>
      </div>

      {/* Reconciliation Results */}
      {reconciliationResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">📊 Last Reconciliation Result</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-gray-600 text-sm">Total Checked</div>
              <div className="text-2xl font-bold">{reconciliationResult.total}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-green-600 text-sm">✅ Successful</div>
              <div className="text-2xl font-bold text-green-700">
                {reconciliationResult.successful}
              </div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="text-red-600 text-sm">❌ Failed</div>
              <div className="text-2xl font-bold text-red-700">
                {reconciliationResult.failed}
              </div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <div className="text-yellow-600 text-sm">⏰ Expired</div>
              <div className="text-2xl font-bold text-yellow-700">
                {reconciliationResult.expired}
              </div>
            </div>
          </div>

          {reconciliationResult.errors && reconciliationResult.errors.length > 0 && (
            <div className="mt-4">
              <h3 className="font-semibold text-red-600 mb-2">Errors:</h3>
              <div className="bg-red-50 p-3 rounded max-h-40 overflow-y-auto">
                {reconciliationResult.errors.map((err, idx) => (
                  <div key={idx} className="text-sm text-red-700 mb-1">
                    • Payment {err.paymentId}: {err.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Refresh Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={fetchDailyReport}
          disabled={loading}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
        >
          {loading ? "⏳ Loading..." : "🔄 Refresh Report"}
        </button>
      </div>
    </div>
  );
};

export default PaymentReconciliation;
